import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../../utils/baseQuery";
import {
  ApiResponse,
  CreateOcOrderRequest,
  OcClient,
  OcCreator,
  OcIssuer,
  OcOrderDetail,
  OcOrderHistoryEntry,
  OcOrderListResponse,
  OcOrdersQueryParams,
  UpdateOcOrderRequest,
} from "../../types/oc";

// El backend de produccion puede ir por detras del frontend: hasta que despliegue el
// catalogo por razon social, GET /oc/clients devuelve la forma antigua (una entrada por
// empresa: id/label/legalName). Se normaliza aqui para que la pagina funcione contra las
// dos formas — con la antigua se ve una sola razon social por empresa, como hasta ahora, y
// en cuanto el backend actualice aparecen todas sin tocar el frontend.
// Se puede retirar cuando el backend con clientBillingId este en produccion.
type LegacyOcClient = Partial<OcClient> & {
  id?: string;
  label?: string;
  legalName?: string;
};

function normalizeOcClient(c: LegacyOcClient): OcClient {
  const companyId = c.companyId ?? c.id ?? "";
  const companyLabel = c.companyLabel ?? c.label ?? "";
  return {
    billingId: c.billingId ?? "",
    companyId,
    companyLabel,
    fiscalName: c.fiscalName ?? c.legalName ?? companyLabel,
    taxId: c.taxId ?? "",
    address: c.address ?? "",
    city: c.city ?? "",
    zip: c.zip ?? "",
    country: c.country ?? "",
    phone: c.phone ?? "",
    email: c.email ?? "",
    website: c.website ?? "",
    paymentTerms: c.paymentTerms ?? "",
    feeVariable: c.feeVariable ?? 0,
    feeFixed: c.feeFixed ?? 0,
  };
}

export const ocApi = createApi({
  reducerPath: "ocApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["OcClients", "OcIssuers", "OcOrders", "OcOrderHistory"],
  endpoints: (builder) => ({
    // GET /api/oc/clients — catálogo de clientes del usuario autenticado
    getOcClients: builder.query<ApiResponse<OcClient[]>, void>({
      query: () => "/oc/clients",
      transformResponse: (resp: ApiResponse<LegacyOcClient[] | null>) => ({
        ...resp,
        data: (resp?.data ?? []).map(normalizeOcClient),
      }),
      providesTags: ["OcClients"],
    }),

    // GET /api/oc/issuers — emisoras (BA / HOD)
    getOcIssuers: builder.query<ApiResponse<OcIssuer[]>, void>({
      query: () => "/oc/issuers",
      providesTags: ["OcIssuers"],
    }),

    // POST /api/oc/issuers/seed — siembra BA/HOD (solo ADMIN/STRATEGY, idempotente)
    seedOcIssuers: builder.mutation<ApiResponse<OcIssuer[]>, void>({
      query: () => ({ url: "/oc/issuers/seed", method: "POST" }),
      invalidatesTags: ["OcIssuers"],
    }),

    // POST /api/oc/orders — crea OC; respuesta = detalle completo con snapshots
    createOcOrder: builder.mutation<
      ApiResponse<OcOrderDetail>,
      CreateOcOrderRequest
    >({
      query: (body) => ({
        url: "/oc/orders",
        method: "POST",
        body,
      }),
      invalidatesTags: ["OcOrders"],
    }),

    // GET /api/oc/orders — histórico paginado con filtros
    getOcOrders: builder.query<
      ApiResponse<OcOrderListResponse>,
      OcOrdersQueryParams | void
    >({
      query: (params) => {
        const search = new URLSearchParams();
        if (params) {
          const {
            page,
            pageSize,
            clientId,
            issuerCompanyId,
            mode,
            startDate,
            endDate,
            search: q,
            createdFrom,
            createdTo,
            createdByUserId,
          } = params;
          if (page != null) search.append("page", String(page));
          if (pageSize != null) search.append("pageSize", String(pageSize));
          if (clientId) search.append("clientId", clientId);
          if (issuerCompanyId)
            search.append("issuerCompanyId", issuerCompanyId);
          if (mode) search.append("mode", mode);
          if (startDate) search.append("startDate", startDate);
          if (endDate) search.append("endDate", endDate);
          if (q) search.append("search", q);
          if (createdFrom) search.append("createdFrom", createdFrom);
          if (createdTo) search.append("createdTo", createdTo);
          if (createdByUserId)
            search.append("createdByUserId", createdByUserId);
        }
        const qs = search.toString();
        return `/oc/orders${qs ? `?${qs}` : ""}`;
      },
      providesTags: ["OcOrders"],
    }),

    // GET /api/oc/creators — quién ha creado alguna OC, para el filtro "Creado por".
    // Con la etiqueta de las OCs: al crear una, alguien nuevo puede entrar en la lista.
    getOcCreators: builder.query<ApiResponse<OcCreator[]>, void>({
      query: () => "/oc/creators",
      providesTags: ["OcOrders"],
    }),

    // GET /api/oc/orders/{id} — detalle completo (snapshots) para regenerar el PDF
    getOcOrderById: builder.query<ApiResponse<OcOrderDetail>, string>({
      query: (id) => `/oc/orders/${id}`,
      providesTags: (_result, _error, id) => [{ type: "OcOrders", id }],
    }),

    // PUT /api/oc/orders/{id} — edita una OC; el nº de OC no cambia y el backend
    // registra el diff en el histórico de cambios
    updateOcOrder: builder.mutation<
      ApiResponse<OcOrderDetail>,
      { id: string; body: UpdateOcOrderRequest }
    >({
      query: ({ id, body }) => ({
        url: `/oc/orders/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "OcOrders",
        { type: "OcOrders", id },
        { type: "OcOrderHistory", id },
      ],
    }),

    // GET /api/oc/orders/{id}/history — quién cambió qué y cuándo
    getOcOrderHistory: builder.query<
      ApiResponse<OcOrderHistoryEntry[]>,
      string
    >({
      query: (id) => `/oc/orders/${id}/history`,
      providesTags: (_result, _error, id) => [{ type: "OcOrderHistory", id }],
    }),

    // POST /api/oc/orders/{id}/duplicate (opcional)
    duplicateOcOrder: builder.mutation<ApiResponse<OcOrderDetail>, string>({
      query: (id) => ({
        url: `/oc/orders/${id}/duplicate`,
        method: "POST",
      }),
      invalidatesTags: ["OcOrders"],
    }),

    // DELETE /api/oc/orders/{id} — elimina una OC (solo ADMIN; 401/403 al resto)
    deleteOcOrder: builder.mutation<ApiResponse<unknown>, string>({
      query: (id) => ({
        url: `/oc/orders/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["OcOrders"],
    }),
  }),
});

export const {
  useGetOcClientsQuery,
  useGetOcIssuersQuery,
  useSeedOcIssuersMutation,
  useCreateOcOrderMutation,
  useUpdateOcOrderMutation,
  useGetOcOrderHistoryQuery,
  useGetOcOrdersQuery,
  useGetOcCreatorsQuery,
  useGetOcOrderByIdQuery,
  useLazyGetOcOrderByIdQuery,
  useDuplicateOcOrderMutation,
  useDeleteOcOrderMutation,
} = ocApi;
